import { useEffect } from 'react';
import { isArray } from '@dimjs/lang';
import { traceGlobalException } from '../exception/exception.js';
import { hideSpinningGlobal, showSpinningGlobal } from '../loading/loading.js';
import { getFullUrlHref } from '../utils/path.js';
import { promiseAllSettled } from '../utils/promise.js';
import { useCallbackRef } from './use-callback-ref.js';

/**
 * We need to make sure that callbacks return one Promise<....> if it's async callback.
 * @example
 * ```ts
 * // correct
 * hooks.useModelEffect([menuActions.loadMenus], true);
 * // correct
 * hooks.useModelEffect([() => moduleActions.loadModuleNormals({})], true);
 * // bad example
 * hooks.useModelEffect([() => {
 *   moduleActions.loadModuleNormals({})
 *  // bad, it return undefined, but loadModuleNormals is async data fetching.
 * }], true);
 * ```
 */
export type ModelEffectCallbacks = (() => unknown) | Array<() => unknown>;
/**
 * Used to initialized actions attached from `@dimjs/models`
 * Only once executed while component did mounted.
 * @param callbacks The callbacks will run immediately
 * @param global default `false` if `true` indicates globally `spinner` and globally `exception`
 */
export function useModelEffect(
  callbacks: ModelEffectCallbacks,
  global = false
): void {
  const urlHref = getFullUrlHref();

  // Handle global spinner
  const setupSpinning = async (showLoading: boolean) => {
    if (global) {
      if (showLoading) {
        await showSpinningGlobal(urlHref);
      } else {
        await hideSpinningGlobal(urlHref);
      }
    }
  };

  // Handle global exceptions
  const traceError = async (err = null) => {
    if (global) {
      await setupSpinning(false);
      await traceGlobalException({
        urlHref,
        error: err,
      });
    }
  };
  const myCallbacks = (isArray(callbacks) ? callbacks : [callbacks]) as Array<
    () => unknown
  >;

  const callback = useCallbackRef(async () => {
    const tasks = myCallbacks.map((fn) => {
      return async () => {
        return await fn();
      };
    });
    try {
      await setupSpinning(true);
      await promiseAllSettled(...tasks);
      await setupSpinning(false);
    } catch (err) {
      await traceError(err as any);
    }
  });
  return useEffect(() => {
    void callback();
    return function cleanup() {
      traceError(null).catch((err) => {
        console.log('useModelEffect cleanup error', err);
      });
    };
  }, []);
}
